import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ShieldCheck, Plus, Receipt, AlertTriangle, TrendingUp, TrendingDown, Users, Tag, Trash2, Pencil } from 'lucide-react';

const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const EMPTY = { streamerId: '', guaranteedCpm: '15', sellCpm: '30', coverage: 'all_video', endDate: '', spendCap: '', active: true, notes: '' };

const StatusChip = ({ g }) => {
  const map = {
    active: 'text-emerald-400 border-emerald-500/25 bg-emerald-500/5',
    paused: 'text-gray-400 border-white/10 bg-white/[0.02]',
    expired: 'text-yellow-400 border-yellow-500/25 bg-yellow-500/5',
    cap_reached: 'text-yellow-400 border-yellow-500/25 bg-yellow-500/5',
    ended: 'text-red-400 border-red-500/25 bg-red-500/5',
  };
  const label = g.active ? 'Live' : (g.status || 'paused').replace('_', ' ');
  return <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${map[g.active ? 'active' : g.status] || map.paused}`}>{label}</span>;
};

export const GuaranteesTab = ({ API, token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [tagTarget, setTagTarget] = useState(null);
  const [tagSellCpm, setTagSellCpm] = useState('30');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [g, c, camps] = await Promise.all([
        axios.get(`${API}/admin/guarantees`, { headers }),
        axios.get(`${API}/admin/guarantees/candidates`, { headers }),
        axios.get(`${API}/admin/campaigns`, { headers }),
      ]);
      setData(g.data); setCandidates(c.data); setCampaigns(camps.data);
    } catch { toast.error('Could not load guarantees'); }
  }, [API, token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        streamerId: editing ? editing.streamerId : form.streamerId,
        guaranteedCpm: parseFloat(form.guaranteedCpm), sellCpm: parseFloat(form.sellCpm),
        coverage: form.coverage, endDate: form.endDate || null,
        spendCap: form.spendCap ? parseFloat(form.spendCap) : null,
        active: form.active, notes: form.notes,
      };
      if (!body.streamerId) { toast.error('Pick a creator'); setSaving(false); return; }
      await axios.post(`${API}/admin/guarantees`, body, { headers });
      toast.success(editing ? 'Guarantee updated' : 'Creator locked in on a guaranteed rate');
      setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const toggle = async (g, active) => {
    try { await axios.patch(`${API}/admin/guarantees/${g.id}`, { active }, { headers }); toast.success(active ? 'Guarantee live' : 'Guarantee paused'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const end = async (g) => {
    try { await axios.delete(`${API}/admin/guarantees/${g.id}`, { headers }); toast.success('Guarantee ended'); load(); }
    catch { toast.error('Failed'); }
  };

  const openLedger = async (g) => {
    try { const r = await axios.get(`${API}/admin/guarantees/${g.id}/ledger`, { headers }); setLedger(r.data); }
    catch { toast.error('Could not load the ledger'); }
  };

  const tagCampaign = async (tagged) => {
    try {
      const r = await axios.post(`${API}/admin/campaigns/${tagTarget.id}/tag-guaranteed`,
        { tagged, sellCpm: tagged ? parseFloat(tagSellCpm) : null, applyToExisting: true }, { headers });
      toast.success(r.data.message, { description: r.data.repricedPlacements ? `${r.data.repricedPlacements} placement(s) repriced to $${tagSellCpm} CPM` : undefined });
      setTagTarget(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const s = data?.summary;

  return (
    <div data-testid="guarantees-tab">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="font-heading text-lg font-bold text-white mb-1 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#FF3B30]" /> Guaranteed Rates</h3>
          <p className="text-xs text-gray-500 max-w-2xl">Lock a creator in at a fixed video CPM paid 100% to them — no revenue split. Brands are billed your sell rate and never see the guarantee. The spread is AYO's margin; anything below the guarantee is logged as a subsidy.</p>
        </div>
        <Button data-testid="add-guarantee-button" onClick={() => { setEditing(null); setForm(EMPTY); setShowForm(true); }} className="bg-[#FF3B30] hover:bg-[#D70015] text-white font-bold text-xs"><Plus className="w-3 h-3 mr-1" />Add Guarantee</Button>
      </div>

      {/* Exposure summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Creators live', value: `${s?.activeCreators ?? 0}/${s?.creators ?? 0}`, icon: Users, cls: 'text-white' },
          { label: 'Guaranteed paid', value: money(s?.guaranteedPaid), icon: ShieldCheck, cls: 'text-white' },
          { label: 'Brand revenue', value: money(s?.brandRevenue), icon: TrendingUp, cls: 'text-white' },
          { label: 'AYO net margin', value: money(s?.netMargin), icon: TrendingUp, cls: (s?.netMargin ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400' },
          { label: 'Subsidy exposure', value: money(s?.subsidy), icon: TrendingDown, cls: (s?.subsidy ?? 0) > 0 ? 'text-red-400' : 'text-gray-400' },
        ].map(c => (
          <div key={c.label} data-testid={`guarantee-stat-${c.label.replace(/\s+/g, '-').toLowerCase()}`}
            className={`p-3 rounded-lg border bg-white/[0.02] ${c.label === 'Subsidy exposure' && (s?.subsidy ?? 0) > 0 ? 'border-red-500/25 bg-red-500/[0.04]' : 'border-white/10'}`}>
            <c.icon className="w-3.5 h-3.5 text-gray-600 mb-1" />
            <p className={`text-xl font-mono font-black ${c.cls}`}>{c.value}</p>
            <p className="text-[9px] text-gray-500 font-mono uppercase tracking-wider">{c.label}</p>
          </div>
        ))}
      </div>

      {(s?.subsidy ?? 0) > 0 && (
        <div className="flex items-start gap-2 mb-6 p-3 rounded-lg border border-red-500/20 bg-red-500/[0.04]">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-300">You're underwater on {money(s.subsidy)} of guaranteed payouts — brands paid less than the guarantee on those deliveries. Raise the sell rate, or cap/pause the guarantee below.</p>
        </div>
      )}

      {/* Guarantee list */}
      <div className="space-y-3 mb-10">
        {!data?.guarantees?.length && <div className="text-center py-12 border border-dashed border-white/10 rounded-lg"><ShieldCheck className="w-8 h-8 text-gray-700 mx-auto mb-2" /><p className="text-sm text-gray-500">No guaranteed creators yet.</p><p className="text-xs text-gray-600 mt-1">Use this to onboard talent: promise a floor, sell the inventory higher.</p></div>}
        {data?.guarantees?.map(g => (
          <div key={g.id} data-testid={`guarantee-row-${g.id}`} className="border border-white/10 rounded-lg bg-[#121212]/50 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-white font-bold">{g.streamerName}</h4>
                  <StatusChip g={g} />
                  {g.underwater && <Badge className="bg-red-500/10 text-red-400 border-red-500/20 font-mono text-[9px] uppercase">Subsidised</Badge>}
                  <Badge className="bg-white/5 text-gray-400 border-white/10 font-mono text-[9px] uppercase">{g.coverage === 'tagged_only' ? 'Tagged campaigns only' : 'All video'}</Badge>
                </div>
                <p className="text-[10px] text-gray-500 font-mono mt-0.5">{g.streamerEmail} · {(g.avgCCV || 0).toLocaleString()} CCV{g.endDate ? ` · ends ${g.endDate}` : ''}</p>
                {g.notes ? <p className="text-[11px] text-gray-500 mt-1 italic">{g.notes}</p> : null}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Switch data-testid={`guarantee-toggle-${g.id}`} checked={!!g.active} onCheckedChange={v => toggle(g, v)} />
                <Button size="sm" variant="ghost" data-testid={`guarantee-ledger-${g.id}`} onClick={() => openLedger(g)} className="text-blue-400 hover:bg-blue-500/10 h-7 text-xs"><Receipt className="w-3 h-3 mr-1" />Ledger</Button>
                <Button size="sm" variant="ghost" data-testid={`guarantee-edit-${g.id}`} onClick={() => { setEditing(g); setForm({ streamerId: g.streamerId, guaranteedCpm: String(g.guaranteedCpm ?? 15), sellCpm: String(g.sellCpm ?? 30), coverage: g.coverage || 'all_video', endDate: g.endDate || '', spendCap: g.spendCap ? String(g.spendCap) : '', active: !!g.active, notes: g.notes || '' }); setShowForm(true); }} className="text-gray-400 hover:bg-white/5 h-7 text-xs"><Pencil className="w-3 h-3 mr-1" />Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => end(g)} className="text-gray-500 hover:text-red-400 hover:bg-red-500/10 h-7 px-2"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>

            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-4 text-center">
              <div><p className="text-sm font-mono font-bold text-[#FF3B30]">${(g.guaranteedCpm || 0).toFixed(2)}</p><p className="text-[9px] text-gray-500 font-mono uppercase">Creator CPM</p></div>
              <div><p className="text-sm font-mono font-bold text-white">${(g.sellCpm || 0).toFixed(2)}</p><p className="text-[9px] text-gray-500 font-mono uppercase">Sell CPM</p></div>
              <div><p className="text-sm font-mono font-bold text-white">{g.deliveries || 0}</p><p className="text-[9px] text-gray-500 font-mono uppercase">Deliveries</p></div>
              <div><p className="text-sm font-mono font-bold text-white">{money(g.guaranteedPaid)}</p><p className="text-[9px] text-gray-500 font-mono uppercase">Paid Out</p></div>
              <div><p className="text-sm font-mono font-bold text-emerald-400">{money(g.platformMargin)}</p><p className="text-[9px] text-gray-500 font-mono uppercase">AYO Margin</p></div>
              <div><p className={`text-sm font-mono font-bold ${(g.subsidy || 0) > 0 ? 'text-red-400' : 'text-gray-500'}`}>{money(g.subsidy)}</p><p className="text-[9px] text-gray-500 font-mono uppercase">Subsidy</p></div>
            </div>

            {g.spendCap ? (
              <div className="mt-3">
                <div className="flex items-center justify-between text-[9px] font-mono text-gray-500 uppercase mb-1"><span>Guarantee cap</span><span>{money(g.guaranteedPaid)} / {money(g.spendCap)}</span></div>
                <Progress value={Math.min(g.capUsedPct || 0, 100)} className="h-1 bg-white/5" />
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {/* Campaign tagging */}
      <div>
        <h3 className="font-heading text-lg font-bold text-white mb-1 flex items-center gap-2"><Tag className="w-4 h-4 text-[#FF3B30]" /> Campaigns in the Programme</h3>
        <p className="text-xs text-gray-500 mb-4">Tag a brand campaign in and set the rate you're selling it at. Repricing updates open placements so the brand is billed your sell rate — the creator still gets their guarantee.</p>
        <div className="space-y-2">
          {campaigns.map(c => (
            <div key={c.id} data-testid={`tag-campaign-row-${c.id}`} className="flex items-center justify-between p-3 rounded border border-white/[0.07] bg-white/[0.02]">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white font-bold truncate">{c.name}</span>
                  {c.guaranteedProgram && <Badge className="bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/20 font-mono text-[9px] uppercase">In programme{c.guaranteedSellCpm ? ` · $${c.guaranteedSellCpm} CPM` : ''}</Badge>}
                </div>
                <p className="text-[10px] text-gray-500 font-mono">{c.adFilename} · budget {money(c.budget)} · spent {money(c.spent)}</p>
              </div>
              <Button size="sm" variant="ghost" data-testid={`tag-campaign-${c.id}`}
                onClick={() => { setTagTarget(c); setTagSellCpm(String(c.guaranteedSellCpm || 30)); }}
                className={`h-7 text-xs ${c.guaranteedProgram ? 'text-gray-400 hover:bg-white/5' : 'text-[#FF3B30] hover:bg-[#FF3B30]/10'}`}>
                {c.guaranteedProgram ? 'Manage' : 'Tag in'}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Add / edit dialog */}
      <Dialog open={showForm} onOpenChange={() => { setShowForm(false); setEditing(null); }}>
        <DialogContent className="bg-[#0d0d0d] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">{editing ? `Edit guarantee — ${editing.streamerName}` : 'New guaranteed rate'}</DialogTitle>
            <DialogDescription className="text-gray-500">Creator is paid the guaranteed CPM in full. Brands are floored at the sell rate.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <div>
                <Label className="text-xs font-mono text-gray-500 uppercase">Creator</Label>
                <Select value={form.streamerId} onValueChange={v => setForm(f => ({ ...f, streamerId: v }))}>
                  <SelectTrigger data-testid="guarantee-creator-select" className="bg-black/40 border-white/10 text-white mt-1"><SelectValue placeholder="Pick a creator" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 max-h-72">
                    {candidates.map(c => <SelectItem key={c.streamerId} value={c.streamerId} className="text-xs">{c.name} · {(c.avgCCV || 0).toLocaleString()} CCV · floor ${c.currentFloorCpm}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono text-gray-500 uppercase">Guaranteed CPM (creator)</Label>
                <Input data-testid="guarantee-cpm-input" type="number" step="0.5" value={form.guaranteedCpm} onChange={e => setForm(f => ({ ...f, guaranteedCpm: e.target.value }))} className="bg-black/40 border-white/10 text-white mt-1" />
              </div>
              <div>
                <Label className="text-xs font-mono text-gray-500 uppercase">Sell CPM (brand)</Label>
                <Input data-testid="guarantee-sell-cpm-input" type="number" step="0.5" value={form.sellCpm} onChange={e => setForm(f => ({ ...f, sellCpm: e.target.value }))} className="bg-black/40 border-white/10 text-white mt-1" />
              </div>
            </div>
            <div className="p-2.5 rounded border border-white/[0.07] bg-white/[0.02] flex items-center justify-between">
              <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">Margin per 1,000 viewers</span>
              <span className={`text-sm font-mono font-bold ${(parseFloat(form.sellCpm || 0) - parseFloat(form.guaranteedCpm || 0)) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {money(parseFloat(form.sellCpm || 0) - parseFloat(form.guaranteedCpm || 0))}
              </span>
            </div>
            <div>
              <Label className="text-xs font-mono text-gray-500 uppercase">Coverage</Label>
              <Select value={form.coverage} onValueChange={v => setForm(f => ({ ...f, coverage: v }))}>
                <SelectTrigger data-testid="guarantee-coverage-select" className="bg-black/40 border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="all_video" className="text-xs">All video ads — a true floor on everything they run</SelectItem>
                  <SelectItem value="tagged_only" className="text-xs">Tagged campaigns only — safer, no exposure elsewhere</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono text-gray-500 uppercase">End date</Label>
                <Input data-testid="guarantee-end-date" type="date" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} className="bg-black/40 border-white/10 text-white mt-1" />
              </div>
              <div>
                <Label className="text-xs font-mono text-gray-500 uppercase">Guarantee cap ($)</Label>
                <Input data-testid="guarantee-spend-cap" type="number" value={form.spendCap} onChange={e => setForm(f => ({ ...f, spendCap: e.target.value }))} placeholder="No cap" className="bg-black/40 border-white/10 text-white mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono text-gray-500 uppercase">Notes</Label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="90-day onboarding guarantee, signed 12 Jun" className="bg-black/40 border-white/10 text-white mt-1" />
            </div>
            <div className="flex items-center justify-between rounded border border-white/[0.07] bg-white/[0.02] px-3 py-2">
              <div><p className="text-[11px] font-mono text-gray-300 uppercase tracking-widest">Guarantee live</p><p className="text-[9px] text-gray-600 font-mono">Off = normal 70/30 split applies</p></div>
              <Switch data-testid="guarantee-active-switch" checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-400 border border-white/10">Cancel</Button>
            <Button data-testid="save-guarantee-button" onClick={save} disabled={saving} className="bg-[#FF3B30] hover:bg-[#D70015] text-white font-bold">{saving ? 'Saving…' : editing ? 'Save changes' : 'Create guarantee'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ledger dialog */}
      <Dialog open={!!ledger} onOpenChange={() => setLedger(null)}>
        <DialogContent className="bg-[#0d0d0d] border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Guarantee ledger — {ledger?.guarantee?.streamerName}</DialogTitle>
            <DialogDescription className="text-gray-500">Every guaranteed delivery: what the brand paid, what the creator got, and the spread.</DialogDescription>
          </DialogHeader>
          {ledger?.rows?.length ? (
            <div className="space-y-1">
              <div className="grid grid-cols-6 gap-2 px-2 py-1 text-[9px] font-mono text-gray-600 uppercase tracking-wider">
                <span>Date</span><span>Brand</span><span className="text-right">Brand CPM</span><span className="text-right">Brand paid</span><span className="text-right">Creator got</span><span className="text-right">Margin</span>
              </div>
              {ledger.rows.map((r, i) => (
                <div key={i} className="grid grid-cols-6 gap-2 px-2 py-1.5 rounded border border-white/[0.05] bg-white/[0.02] text-[11px] font-mono">
                  <span className="text-gray-400">{(r.date || '').slice(5, 16).replace('T', ' ')}</span>
                  <span className="text-white truncate">{r.brandName || '—'}</span>
                  <span className="text-right text-gray-400">${(r.brandCpm || 0).toFixed(2)}</span>
                  <span className="text-right text-white">{money(r.brandCharge)}</span>
                  <span className="text-right text-[#FF3B30]">{money(r.creatorPayout)}</span>
                  <span className={`text-right ${r.subsidy > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{r.subsidy > 0 ? `-${money(r.subsidy)}` : money(r.margin)}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-gray-500 py-6 text-center">No guaranteed deliveries recorded yet.</p>}
        </DialogContent>
      </Dialog>

      {/* Campaign tag dialog */}
      <Dialog open={!!tagTarget} onOpenChange={() => setTagTarget(null)}>
        <DialogContent className="bg-[#0d0d0d] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Guaranteed programme — {tagTarget?.name}</DialogTitle>
            <DialogDescription className="text-gray-500">Set the CPM this brand is billed. Guaranteed creators keep their own rate regardless.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs font-mono text-gray-500 uppercase">Sell CPM (brand-facing)</Label>
            <Input data-testid="tag-sell-cpm-input" type="number" step="0.5" value={tagSellCpm} onChange={e => setTagSellCpm(e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" />
            <p className="text-[10px] text-gray-600 font-mono mt-2">Open video placements on this campaign are repriced to this CPM.</p>
          </div>
          <DialogFooter>
            {tagTarget?.guaranteedProgram && <Button variant="ghost" onClick={() => tagCampaign(false)} className="text-gray-400 border border-white/10">Remove from programme</Button>}
            <Button data-testid="confirm-tag-campaign" onClick={() => tagCampaign(true)} className="bg-[#FF3B30] hover:bg-[#D70015] text-white font-bold">{tagTarget?.guaranteedProgram ? 'Update rate' : 'Tag in & reprice'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
